import api from './api';
import oktaAuth from '@/config/okta';
import type {
  SearchSuggestion,
  SearchResult,
  SearchFacetsResponse,
  SearchStatusResponse,
  ScoringProfile,
  SynonymMap,
  SynonymFieldAssignment,
  SynonymFieldAssignmentUpdate,
  SearchQuery,
  CleSearchQuery,
  CpeSearchQuery,
  SkillsSearchQuery,
  SimulationSearchQuery,
  SimulationSearchResult,
  CleSearchResult,
  CpeSearchResult,
  SkillsSearchResult,
  TypedSearchResponse,
  AdminCatalogSearchQuery,
  IndexingLogsResponse,
  RelevanceEvaluationResult,
} from '@/types/search';
import type { CourseIdListResult } from '@/types/catalog';
import { buildQueryString } from '@/utils/buildQueryString';

/**
 * The typed /courses URL. Multi-value filters (competency, jurisdictions,
 * credit type, practice area, field of study) are appended as repeated keys
 * rather than passed through buildQueryString — that helper stringifies an
 * array into a comma list, which is wrong for display names that contain a
 * comma ("Diversity, Inclusion & Elimination of Bias"). The backend reads
 * repeated keys for every multi-value filter.
 */
export function typedCoursesUrl(libraryId: number, params: object): string {
  const scalars: Record<string, unknown> = {};
  const repeated: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const v of value) repeated.push(`${key}=${encodeURIComponent(String(v))}`);
    } else {
      scalars[key] = value;
    }
  }

  const qs = buildQueryString(scalars);
  const tail = repeated.join('&');
  return `/libraries/${libraryId}/courses${qs}${tail ? (qs ? '&' : '?') + tail : ''}`;
}

/**
 * /api/libraries/{id}/courses shapes its rows by the library's kind. The
 * caller chose its result type from the kind it believes the library has, so
 * a discriminator mismatch means that belief is stale — fail loudly rather
 * than let mis-shaped rows flow into the mappers.
 */
function assertSearchKind<T extends { kind: string }>(data: T, expected: string): T {
  if (data.kind !== expected) {
    throw new Error(`Expected ${expected} search results but the library returned ${data.kind}.`);
  }
  return data;
}

export const searchApi = {
  // ── Search ─────────────────────────────────────────────────────────────────

  /**
   * Type-ahead suggestions. Calls /api/search/suggestions.
   * Falls back to SQL search on the backend when AI Search is disabled.
   */
  getSuggestions: async (q: string, limit = 5): Promise<SearchSuggestion[]> => {
    const response = await api.get<SearchSuggestion[]>(
      `/search/suggestions${buildQueryString({ q, limit })}`,
    );
    return response.data;
  },

  /**
   * Full hybrid search with facets and scoring.
   * Returns 503 { code: 'SEARCH_UNAVAILABLE' } when the index can't answer —
   * callers surface it via toSearchError; there is no SQL fallback (BPE-770).
   * Optional AbortSignal lets callers cancel a superseded request.
   */
  search: async (query: SearchQuery, options?: { signal?: AbortSignal }): Promise<SearchResult> => {
    const response = await api.get<SearchResult>(
      `/search${buildQueryString(query as Record<string, unknown>)}`,
      options?.signal ? { signal: options.signal } : undefined,
    );
    return response.data;
  },

  // ── Typed learner search (BPE-599) ─────────────────────────────────────────
  // One route — /api/libraries/{id}/courses — where the library's declared
  // kind selects the filter contract and result shape. The three methods here
  // keep the per-kind caller contracts and assert the response's `kind`
  // discriminator, so a stale cached library kind fails fast instead of
  // silently mis-casting rows. 404 when the library doesn't exist or has no
  // typed surface; 503 SEARCH_UNAVAILABLE when the index can't answer —
  // callers show the search-unavailable state.

  searchCle: async (query: CleSearchQuery, options?: { signal?: AbortSignal }): Promise<TypedSearchResponse<CleSearchResult>> => {
    const { libraryId, ...params } = query;
    const response = await api.get<TypedSearchResponse<CleSearchResult>>(
      typedCoursesUrl(libraryId, params),
      options?.signal ? { signal: options.signal } : undefined,
    );
    return assertSearchKind(response.data, 'CLE');
  },

  searchCpe: async (query: CpeSearchQuery, options?: { signal?: AbortSignal }): Promise<TypedSearchResponse<CpeSearchResult>> => {
    const { libraryId, ...params } = query;
    const response = await api.get<TypedSearchResponse<CpeSearchResult>>(
      typedCoursesUrl(libraryId, params),
      options?.signal ? { signal: options.signal } : undefined,
    );
    return assertSearchKind(response.data, 'CPE');
  },

  searchSkills: async (query: SkillsSearchQuery, options?: { signal?: AbortSignal }): Promise<TypedSearchResponse<SkillsSearchResult>> => {
    const { libraryId, ...params } = query;
    const response = await api.get<TypedSearchResponse<SkillsSearchResult>>(
      `/libraries/${libraryId}/courses${buildQueryString(params as unknown as Record<string, unknown>)}`,
      options?.signal ? { signal: options.signal } : undefined,
    );
    return assertSearchKind(response.data, 'Skills');
  },

  searchSimulations: async (query: SimulationSearchQuery, options?: { signal?: AbortSignal }): Promise<TypedSearchResponse<SimulationSearchResult>> => {
    const { libraryId, ...params } = query;
    const response = await api.get<TypedSearchResponse<SimulationSearchResult>>(
      typedCoursesUrl(libraryId, params),
      options?.signal ? { signal: options.signal } : undefined,
    );
    return assertSearchKind(response.data, 'Simulation');
  },

  // ── Admin catalog search (BPE-599) ─────────────────────────────────────────
  // The course picker's cross-type search. Same result item shape as the old
  // generic search, served from /api/admin/search (org-admin and above).

  adminSearch: async (query: AdminCatalogSearchQuery): Promise<SearchResult> => {
    const response = await api.get<SearchResult>(
      `/admin/search${buildQueryString(query as Record<string, unknown>)}`,
    );
    return response.data;
  },

  /**
   * Returns the ids (+titles) of every course matching the admin search, capped
   * server-side, for the picker's "select all matching" action. The index stores
   * ids as strings; they're normalized to numbers so the result matches the SQL
   * path's shape.
   */
  adminSearchIds: async (query: AdminCatalogSearchQuery): Promise<CourseIdListResult> => {
    const response = await api.get<{
      items: { id: string; title: string }[];
      totalCount: number;
      truncated: boolean;
    }>(`/admin/search/ids${buildQueryString(query as Record<string, unknown>)}`);
    return {
      items: response.data.items.map((i) => ({ id: parseInt(i.id, 10), title: i.title })),
      totalCount: response.data.totalCount,
      truncated: response.data.truncated,
    };
  },

  /**
   * Returns available facet counts for the current user's library scope.
   * Returns null (not throws) when AI Search is unavailable.
   */
  /**
   * Facet counts used as filter-option lists. Pass `libraryId` to scope the values
   * to one library and to that library's kind (BPE-598 D7) — unscoped, a curated
   * library offers options drawn from every accessible library, most of which
   * return nothing when selected. Omit it for the cross-type case (the picker).
   */
  getFacets: async (libraryId?: number): Promise<SearchFacetsResponse | null> => {
    try {
      const response = await api.get<SearchFacetsResponse>(
        `/search/facets${libraryId != null ? `?libraryId=${libraryId}` : ''}`,
      );
      return response.data;
    } catch {
      return null;
    }
  },

  // ── Admin ──────────────────────────────────────────────────────────────────

  getStatus: async (): Promise<SearchStatusResponse> => {
    const response = await api.get<SearchStatusResponse>('/search/admin/status');
    return response.data;
  },

  triggerReindex: async (): Promise<void> => {
    await api.post('/search/admin/reindex');
  },

  /**
   * BPE-813: puts one course back in the indexer's queue, clearing its embedding
   * retry state, so the next incremental pass processes it. Rejects with a 404
   * for a course that no longer exists.
   */
  requeueCourse: async (learningObjectId: string): Promise<void> => {
    await api.post(`/search/admin/reindex/${encodeURIComponent(learningObjectId)}`);
  },

  getScoringProfiles: async (): Promise<ScoringProfile[]> => {
    const response = await api.get<ScoringProfile[]>('/search/admin/profiles');
    return response.data;
  },

  getSynonymMaps: async (): Promise<SynonymMap[]> => {
    const response = await api.get<SynonymMap[]>('/search/admin/synonyms');
    return response.data;
  },

  updateSynonymMap: async (name: string, synonyms: string): Promise<void> => {
    await api.put(`/search/admin/synonyms/${encodeURIComponent(name)}`, { synonyms });
  },

  deleteSynonymMap: async (name: string): Promise<void> => {
    await api.delete(`/search/admin/synonyms/${encodeURIComponent(name)}`);
  },

  getSynonymFieldAssignments: async (): Promise<SynonymFieldAssignment[]> => {
    const response = await api.get<SynonymFieldAssignment[]>('/search/admin/synonyms/fields');
    return response.data;
  },

  updateSynonymFieldAssignments: async (assignments: SynonymFieldAssignmentUpdate[]): Promise<void> => {
    await api.put('/search/admin/synonyms/fields', { assignments });
  },

  getLogs: async (): Promise<IndexingLogsResponse | null> => {
    try {
      const response = await api.get<IndexingLogsResponse>('/search/admin/logs');
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Records a course click/selection from search results.
   * Fire-and-forget — errors are silently swallowed so they never affect navigation.
   * Uses fetch with keepalive so the request survives SPA navigation.
   */
  logSelection: (searchEventId: number, learningObjectId: number, learningObjectTitle: string, position: number): void => {
    const url = `/api/search/events/${searchEventId}/select`;
    const body = JSON.stringify({ learningObjectId, learningObjectTitle, position });
    // Raw fetch is used (instead of the axios `api` instance) so keepalive lets the
    // request finish after the SPA navigates to the course page. Axios doesn't expose
    // keepalive, so we attach the Okta bearer manually — without it the endpoint
    // returns 401 and the catch swallows it, leaving SearchSelections silently empty.
    const token = oktaAuth.getAccessToken();
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {});
  },

  /**
   * Runs the ground-truth relevance evaluation. Pass `kind` to score against that
   * Product Type's eval profile and candidate set (BPE-598 D8) — without it the run
   * uses CLE's profile, so a Skills CSV would be measured against CLE's weights.
   */
  evaluateRelevance: async (file: File, kind?: string): Promise<RelevanceEvaluationResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<RelevanceEvaluationResult>(
      `/search/admin/evaluate-relevance${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
};
